import { useEffect, useRef, useState } from "react";

// A curated, bakery-leaning emoji set — no external dependency, no full unicode
// keyboard. Enough variety to give each quiz a personality.
const EMOJIS = [
  // Breads first — lead with the bakery.
  "🥐", "🥖", "🍞", "🥨", "🥯", "🫓", "🥪", "🥙",
  // Pastries & cakes.
  "🧁", "🍰", "🎂", "🥧", "🍪", "🍩", "🍮", "🥞",
  // Sweet treats.
  "🧇", "🍦", "🍨", "🍯", "🍫", "🍓", "🍒", "🫐",
  // Drinks.
  "☕", "🫖", "🍵", "🧋", "🥛", "🧃", "🍷", "🥂",
  // Ingredients.
  "🌾", "🧂", "🧈", "🥚", "🍳", "🧀", "🥜", "🌰",
  // Chefs & celebration.
  "👨‍🍳", "👩‍🍳", "🔥", "⭐", "🏆", "🎉", "🎯", "🎀",
  // A little fun.
  "🧠", "💡", "🚀", "❤️", "🥳", "😀", "🤓", "👏",
];

/** Emoji "cover" picker for a quiz — replaces image upload. Shows the current
    emoji on a tile; clicking opens a curated grid. Clear returns to no emoji. */
export function EmojiPicker({
  value,
  onChange,
}: {
  value?: string;
  onChange: (emoji: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative", flex: "0 0 auto" }}>
      <button
        type="button"
        title="Choose a quiz emoji"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 46,
          height: 46,
          borderRadius: 13,
          border: "1px solid var(--atl-line)",
          background: value ? "var(--atl-surface-2)" : "var(--atl-surface)",
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          fontSize: value ? 24 : 18,
          lineHeight: 1,
          padding: 0,
        }}
      >
        {value || <span style={{ color: "var(--atl-faint)", fontSize: 22, fontWeight: 700 }}>+</span>}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            zIndex: 60,
            width: 296,
            background: "var(--atl-surface)",
            border: "1px solid var(--atl-line)",
            borderRadius: 16,
            boxShadow: "0 16px 40px rgba(40,25,15,.18)",
            padding: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <span className="atl-eyebrow">Quiz emoji</span>
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange(undefined);
                  setOpen(false);
                }}
                style={{
                  marginLeft: "auto",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "var(--atl-accent)",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "var(--atl-sans)",
                }}
              >
                Clear
              </button>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 4 }}>
            {EMOJIS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => {
                  onChange(e);
                  setOpen(false);
                }}
                title={e}
                style={{
                  height: 32,
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  background: value === e ? "var(--atl-gold-soft)" : "transparent",
                  fontSize: 19,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
